import React, { useEffect } from "react";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { TeamLeadSolutionType } from "@src/constants";
import { ForwardSelection } from "@src/features/staffing_request_details/components/forward_selection";
import {
  clearNextStepModalState,
  resetSolutionState,
  setConfirmationButtonDisableStatus,
  setNextStepModalVisible,
} from "@src/services/nextStepModalSlice";
import { setDetailsModalVisible } from "@src/services/requestOverviewSlice";
import { useAddNoteMutation } from "@src/services/slices/notesApiSlice";
import {
  useRejectRequestMutation,
  useForwardRequestMutation,
  useProposeTeamMemberMutation,
  useSuggestDifferentWorkloadMutation,
  useTakeBackProposalMutation,
  useTeamLeadDeclineRequestMutation,
  useUpdateInRecruitmentMutation,
} from "@src/services/slices/teamLeadsApi";
import { RootState, useAppDispatch, useAppSelector } from "@src/setupStore";
import {
  Employee,
  Note,
  ProposeAllocation,
  RequestOverviewDetails,
} from "@src/types";
import { getInitials } from "@src/utils/helper";
import { App, Avatar, Form, Modal, Input } from "antd";
import "./next_step_modal.less";

import { createProposeDifferentWorkloadPayload } from "../../utils/propose_allocations_utils";
import { DeclineProposeTable } from "../decline_propose_table";
import { ProposeTeamMemberOverviewTable } from "../propose_team_member_overview_table";

interface NextStepModalProps {
  request: RequestOverviewDetails;
  loggedInUser: Employee;
}

const NextStepModal: React.FC<NextStepModalProps> = ({
  request,
  loggedInUser,
}) => {
  const [form] = Form.useForm<{ comment: string }>();

  const { message } = App.useApp();

  const {
    nextStepModalVisible,
    confirmationButtonDisabled,
    proposedSolutionType,
    proposeTeamMember,
    forward,
    decline,
  } = useAppSelector((state: RootState) => state.nextStepModalSlice);

  useEffect(() => {
    if (
      proposedSolutionType === TeamLeadSolutionType.IN_RECRUITMENT ||
      proposedSolutionType === TeamLeadSolutionType.REMOVE_IN_RECRUITMENT ||
      proposedSolutionType === TeamLeadSolutionType.TAKE_BACK_PROPOSAL ||
      proposedSolutionType === TeamLeadSolutionType.DECLINE ||
      proposedSolutionType === TeamLeadSolutionType.REJECT_FORWARDED_REQUEST
    ) {
      dispatch(setConfirmationButtonDisableStatus(false));
    }
  }, [proposedSolutionType]);

  //local states

  const dispatch = useAppDispatch();

  //hooks
  const [forwardRequest] = useForwardRequestMutation();
  const [addNote] = useAddNoteMutation();
  const [proposeNewTeamMember] = useProposeTeamMemberMutation();
  const [updateInRecruitment] = useUpdateInRecruitmentMutation();
  const [declineRequest] = useTeamLeadDeclineRequestMutation();
  const [takeBackProposal] = useTakeBackProposalMutation();
  const [suggestDifferentWorkload] = useSuggestDifferentWorkloadMutation();
  const [reject] = useRejectRequestMutation();

  //local variables
  const requestTitle = `${request.projectRoleRequestId} - ${request.genericRole}`;
  const isSmallModal: boolean =
    proposedSolutionType === TeamLeadSolutionType.IN_RECRUITMENT ||
    proposedSolutionType === TeamLeadSolutionType.REMOVE_IN_RECRUITMENT ||
    proposedSolutionType === TeamLeadSolutionType.FORWARD ||
    proposedSolutionType === TeamLeadSolutionType.DECLINE ||
    proposedSolutionType === TeamLeadSolutionType.REJECT_FORWARDED_REQUEST ||
    proposedSolutionType === TeamLeadSolutionType.TAKE_BACK_PROPOSAL;

  /**
   * Handles the cancellation of the next step Modal.
   */
  function onCancelNextStepModal() {
    dispatch(setNextStepModalVisible(false));
    dispatch(resetSolutionState());
  }

  /**
   * Handles the confirmation of the next step Modal and call the appropriate function based on the selected solution.
   *
   */
  async function onConfirmSend() {
    const note: string = form.getFieldValue("comment");

    switch (proposedSolutionType) {
      case TeamLeadSolutionType.PROPOSE_TEAM_MEMBER:
      case TeamLeadSolutionType.VACANCY:
        !proposeTeamMember?.proposedAllocations?.size
          ? await handleProposeTeamMember()
          : await handleProposeTeamMemberWithNewAllocation();
        break;
      case TeamLeadSolutionType.FORWARD:
        await handleForwardRequestConfirmation();
        break;
      case TeamLeadSolutionType.IN_RECRUITMENT:
        await handleInRecruitmentConfirmation(true);
        break;
      case TeamLeadSolutionType.REMOVE_IN_RECRUITMENT:
        await handleInRecruitmentConfirmation(false);
        break;
      case TeamLeadSolutionType.TAKE_BACK_PROPOSAL:
        await handleTakeBackProposal();
        break;
      case TeamLeadSolutionType.DECLINE:
        await handleDeclineRequestConfirmation("propose");
        break;
      case TeamLeadSolutionType.DECLINE_AND_PROPOSE:
        await handleDeclineRequestWithNewAllocations();
        break;
      case TeamLeadSolutionType.REJECT_FORWARDED_REQUEST:
        await handleReject();
        break;
      default:
        break;
    }

    if (note) await createNote(note);
    dispatch(setDetailsModalVisible(false));
    dispatch(setNextStepModalVisible(false));
    dispatch(clearNextStepModalState());
  }

  /**
   *  Handles the forward request solution. This will forward the request to another team lead.
   */
  async function handleForwardRequestConfirmation() {
    try {
      await forwardRequest({
        roleRequestId: request.projectRoleRequestId,
        teamLeadId: forward.forwardedTeamLeadId,
      }).unwrap();
      message.success("Request forwarded successfully");
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Handles the marking or removing in recruitment solution. This will mark the request as in recruitment.
   */
  async function handleInRecruitmentConfirmation(inRecruitment: boolean) {
    try {
      await updateInRecruitment({
        roleRequestId: request.projectRoleRequestId,
        inRecruitment: inRecruitment,
      }).unwrap();
      const operationText = inRecruitment ? `marked as` : `removed from`;
      message.success(`Request ${operationText} in recruitment successfully`);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Handles the take back proposal solution. This will take back the proposal.
   */
  async function handleTakeBackProposal() {
    try {
      await takeBackProposal({
        roleRequestId: request.projectRoleRequestId,
      }).unwrap();
      message.success("Proposal taken back successfully");
    } catch (error) {
      console.error("Failed to take back proposal", error);
    }
  }

  /**
   * Handles the decline request solution. This will decline the request and propose a different workload.
   *
   */
  async function handleProposeTeamMember() {
    try {
      await proposeNewTeamMember({
        roleRequestId: request.projectRoleRequestId,
        teamMemberId: proposeTeamMember.selectedTeamMemberId,
      }).unwrap();
      message.success("selected team member successfully proposed");
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Handle proposeNewTeamMember with new allocation. This will propose a different workload for the selected team member.
   */
  async function handleProposeTeamMemberWithNewAllocation() {
    const payload = createProposeDifferentWorkloadPayload(
      proposeTeamMember.proposedAllocations
    );

    try {
      await suggestDifferentWorkload({
        id: request.projectRoleRequestId,
        selectedMemberId: proposeTeamMember.selectedTeamMemberId,
        requestAllocationPayload: payload,
      }).unwrap();
      message.success(
        "selected team member and allocation successfully proposed"
      );
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Handle the decline request solution. This will decline the request.
   */
  async function handleDeclineRequestConfirmation(declineRequestType: string) {
    try {
      await declineRequest({
        payLoad: {
          id: request.projectRoleRequestId,
          requestAllocationPayload: [],
        },
        params: { declineRequestType: declineRequestType },
      }).unwrap();
      message.success("Request declined successfully");
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Handle the decline request solution. This will decline the request and propose a different workload.
   */
  async function handleDeclineRequestWithNewAllocations() {
    const payload: ProposeAllocation[] = createProposeDifferentWorkloadPayload(
      decline.proposedAllocations
    );

    try {
      await declineRequest({
        payLoad: {
          id: request.projectRoleRequestId,
          requestAllocationPayload: payload,
        },
        params: { declineRequestType: "propose" },
      }).unwrap();
      message.success("Request declined successfully");
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Handle the reject request solution. This will reject the request and send it back to the team lead.
   */
  async function handleReject() {
    try {
      await reject(request.projectRoleRequestId).unwrap();
      message.success("Request rejected successfully");
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Creates a note and save it.
   *
   * @param noteText  The note text to be saved.
   */
  async function createNote(noteText: string) {
    const note: Note = {
      employeeId: loggedInUser.employeeId,
      createdDate: new Date().toISOString(),
      roleRequestId: request.projectRoleRequestId,
      text: noteText,
      employeeFullName: loggedInUser.fullName,
      createdBy: loggedInUser.employeeId,
    };

    addNote(note)
      .unwrap()
      .then(() => {
        message.success("Successfully saved note", 2);
      })
      .catch((err) => console.log("error", err));
  }

  /**
   * Generates the children for the next step modal based on the selected solution.
   */
  function generateChildren() {
    switch (proposedSolutionType) {
      case TeamLeadSolutionType.PROPOSE_TEAM_MEMBER:
      case TeamLeadSolutionType.VACANCY:
        return (
          <ProposeTeamMemberOverviewTable
            request={request.childRequest ?? request}
            teamLeadId={loggedInUser.employeeId}
            solutionType={proposedSolutionType}
          />
        );
      case TeamLeadSolutionType.FORWARD:
        return <ForwardSelection />;
      case TeamLeadSolutionType.IN_RECRUITMENT:
        return (
          <p>{`Are you sure you want to mark ${requestTitle} as in recruitment.`}</p>
        );
      case TeamLeadSolutionType.REMOVE_IN_RECRUITMENT:
        return (
          <p>{`This action will change the request status back to 'Requested'. Are you sure you want to remove ${requestTitle} from the recruitment list?`}</p>
        );
      case TeamLeadSolutionType.TAKE_BACK_PROPOSAL:
        return (
          <>
            <p>
              Are you sure you want to take back{" "}
              <span className="bold">{request.assignedTeamMember}?</span>
            </p>
            <p>
              {" "}
              Leave a comment for the project lead{" "}
              <span className="bold">{request.projectLead}</span>.
            </p>
          </>
        );
      case TeamLeadSolutionType.DECLINE:
        return null;
      case TeamLeadSolutionType.DECLINE_AND_PROPOSE:
        return (
          <DeclineProposeTable request={request.childRequest ?? request} />
        );
      case TeamLeadSolutionType.REJECT_FORWARDED_REQUEST:
        return (
          <div data-testid="description">
            <p>
              Are you sure you want to reject the request? Request will be send
              back to{" "}
              <span className="bold">
                {request.primaryTeamLeadEmployeeName}
              </span>
              .
            </p>
            <p>
              Leave a comment for the project lead{" "}
              <span className="bold">{request.projectLead}</span>.
            </p>
          </div>
        );
      default:
        break;
    }
  }

  /**
   * Generates the title for the next step modal based on the selected solution.
   */
  function generateTitle() {
    switch (proposedSolutionType) {
      case TeamLeadSolutionType.PROPOSE_TEAM_MEMBER: {
        return `Propose team member(s) for ${requestTitle}`;
      }
      case TeamLeadSolutionType.VACANCY: {
        return `Select Vacancy for ${requestTitle}`;
      }
      case TeamLeadSolutionType.FORWARD:
        return `Forward request ${requestTitle}`;
      case TeamLeadSolutionType.IN_RECRUITMENT:
        return `Mark '${requestTitle}' as in recruitment`;
      case TeamLeadSolutionType.REMOVE_IN_RECRUITMENT:
        return `Remove '${requestTitle}' from in recruitment`;
      case TeamLeadSolutionType.TAKE_BACK_PROPOSAL:
        return `Take back proposal`;
      case TeamLeadSolutionType.DECLINE:
        return `Decline ${requestTitle}`;
      case TeamLeadSolutionType.DECLINE_AND_PROPOSE:
        return `Decline ${requestTitle}`;
      case TeamLeadSolutionType.REJECT_FORWARDED_REQUEST:
        return `Reject forwarded request`;
      default:
        break;
    }
  }

  return (
    <Modal
      open={nextStepModalVisible}
      centered
      width={isSmallModal ? 700 : 1018}
      maskClosable={false}
      closable={false}
      title={generateTitle()}
      okButtonProps={{
        icon: <CheckOutlined />,
        size: "large",
        disabled: confirmationButtonDisabled,
        "data-testid": "confirm-button",
      }}
      onOk={() => onConfirmSend()}
      okText="Confirm"
      cancelText="Close"
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: "large",
        "data-testid": "close-next-step-modal",
      }}
      onCancel={onCancelNextStepModal}
      className="next-step-modal"
    >
      {generateChildren()}
      <div className="next-step-modal__comment">
        <Form layout="vertical" form={form}>
          <Form.Item label="Add Comment" name="comment">
            <div className="avatar-comment-section">
              <Avatar>{getInitials(loggedInUser.fullName)}</Avatar>
              <Input.TextArea
                autoSize={false}
                rows={4}
                style={{ resize: "none" }}
                data-testid="comment-input"
              />
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default NextStepModal;
